// hooks/useAdBoosts.ts
//
// Pay-per-boost (a la carte) purchase flow for a single ad. Mirrors the
// pending -> manual-admin-activation pattern used by useSubscripitions.tsx:
// a boost is inserted with status 'pending', the seller is sent to the
// existing WhatsApp payment-instructions flow, and an admin flips it to
// 'active' once the screenshot is verified (see AdBoostsTable.tsx).
import { useCallback, useState } from 'react';
import supabase from '../config/supabaseConfig';
import { useAuth } from './useAuth';

// Defaults approved alongside the 60% subscription price cut: a single
// boost costs 500 XOF and lasts 5 days. Kept here as the single source of
// truth so the buyer UI and any future admin UI agree on the numbers.
export const BOOST_PRICE_XOF = 500;
export const BOOST_DURATION_DAYS = 5;

export type AdBoostStatus = 'pending' | 'active' | 'expired' | 'rejected' | 'cancelled';

export interface AdBoost {
    id: string;
    ad_id: string;
    user_id: string;
    amount: number;
    duration_days: number;
    status: AdBoostStatus;
    requested_at: string;
    activated_at: string | null;
    expires_at: string | null;
    created_at: string;
    updated_at: string;
}

export function useAdBoosts() {
    const { user } = useAuth();
    const [requestingAdId, setRequestingAdId] = useState<string | null>(null);

    // Creates (or reuses) a pending/active boost request for an ad. Returns
    // the existing row instead of inserting a duplicate if the ad already
    // has a request in flight.
    const requestBoost = useCallback(async (adId: string): Promise<AdBoost> => {
        if (!user?.id) {
            throw new Error('Utilisateur non authentifié');
        }

        setRequestingAdId(adId);
        try {
            const { data: existing, error: existingError } = await supabase
                .from('ad_boosts')
                .select('*')
                .eq('ad_id', adId)
                .in('status', ['pending', 'active'])
                .maybeSingle();

            if (existingError) throw existingError;
            if (existing) {
                return existing as AdBoost;
            }

            const { data, error } = await supabase
                .from('ad_boosts')
                .insert([{
                    ad_id: adId,
                    user_id: user.id,
                    amount: BOOST_PRICE_XOF,
                    duration_days: BOOST_DURATION_DAYS,
                    status: 'pending',
                }])
                .select()
                .single();

            if (error) {
                // 23505 = unique_violation on ad_boosts_one_open_per_ad: another
                // request for this ad won the race between our existence check
                // above and this insert (double-click, two tabs, etc). Fall back
                // to whatever request won instead of surfacing a raw DB error.
                if (error.code === '23505') {
                    const { data: winner, error: refetchError } = await supabase
                        .from('ad_boosts')
                        .select('*')
                        .eq('ad_id', adId)
                        .in('status', ['pending', 'active'])
                        .maybeSingle();
                    if (refetchError) throw refetchError;
                    if (winner) return winner as AdBoost;
                }
                throw error;
            }
            return data as AdBoost;
        } finally {
            setRequestingAdId(null);
        }
    }, [user?.id]);

    return { requestBoost, requestingAdId };
}
